import { JSX, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Building2,
  Users,
  UsersRound,
  Megaphone,
  Plane,
  Image,
  Layers,
  Newspaper,
  Shapes,
  Languages,
  Target,
} from "lucide-react";
import Layout from "@/components/layout/Layout";
import Spinner from "@/components/ui/Spinner";
import useAuthStore from "@/store/authStore";
import useGlobalStore from "@/store/globalStore";
import useAdminStatsStore, {
  AdminStats,
  AgencyRow,
} from "./store/adminStatsStore";
import BarChart, { BarDatum } from "./components/BarChart";
import DonutChart, { Slice } from "./components/DonutChart";

type AgencyMetric = "campaigns" | "users" | "teams" | "languages" | "objectives";

const METRICS: { key: AgencyMetric; label: string }[] = [
  { key: "campaigns", label: "Campaigns" },
  { key: "users", label: "Users" },
  { key: "teams", label: "Teams" },
  { key: "languages", label: "Languages" },
  { key: "objectives", label: "Objectives" },
];

const toChartData = (rows: AgencyRow[], metric: AgencyMetric): BarDatum[] =>
  [...rows]
    .map((r) => ({ label: r.agency_name, value: r[metric] }))
    .sort((a, b) => b.value - a.value);

const catalogSlices = (s: AdminStats): Slice[] => [
  { label: "Ad Categories", value: s.catalog.ad_categories },
  { label: "Publishers", value: s.catalog.publishers },
  { label: "Ad Types", value: s.catalog.ad_types },
  { label: "Languages", value: s.catalog.languages },
  { label: "Objectives", value: s.catalog.objectives },
];

const deliverySlices = (s: AdminStats): Slice[] => [
  { label: "Campaigns", value: s.delivery.campaigns },
  { label: "Flights", value: s.delivery.flights },
  { label: "Assets", value: s.delivery.assets },
];

const peopleSlices = (s: AdminStats): Slice[] => [
  { label: "Users", value: s.people.users },
  { label: "Teams", value: s.people.teams },
];

type Card = {
  label: string;
  value: number;
  icon: JSX.Element;
};

const buildSections = (s: AdminStats) => [
  {
    title: "Agencies",
    cards: [
      {
        label: "Media Agencies",
        value: s.agencies.media_agencies,
        icon: <Building2 size={22} />,
      },
    ] as Card[],
  },
  {
    title: "People",
    cards: [
      { label: "Users", value: s.people.users, icon: <Users size={22} /> },
      { label: "Teams", value: s.people.teams, icon: <UsersRound size={22} /> },
    ] as Card[],
  },
  {
    title: "Delivery",
    cards: [
      {
        label: "Campaigns",
        value: s.delivery.campaigns,
        icon: <Megaphone size={22} />,
      },
      { label: "Flights", value: s.delivery.flights, icon: <Plane size={22} /> },
      { label: "Assets", value: s.delivery.assets, icon: <Image size={22} /> },
    ] as Card[],
  },
  {
    title: "Catalog",
    cards: [
      {
        label: "Ad Categories",
        value: s.catalog.ad_categories,
        icon: <Layers size={22} />,
      },
      {
        label: "Publishers",
        value: s.catalog.publishers,
        icon: <Newspaper size={22} />,
      },
      {
        label: "Ad Types",
        value: s.catalog.ad_types,
        icon: <Shapes size={22} />,
      },
      {
        label: "Languages",
        value: s.catalog.languages,
        icon: <Languages size={22} />,
      },
      {
        label: "Objectives",
        value: s.catalog.objectives,
        icon: <Target size={22} />,
      },
    ] as Card[],
  },
];

function StatCard({ label, value, icon }: Card) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center gap-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-pink-50 text-[#EA33C0] shrink-0">
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-3xl font-semibold text-black leading-none">
          {value.toLocaleString()}
        </span>
        <span className="text-sm text-gray-500 mt-1">{label}</span>
      </div>
    </div>
  );
}

export default function AdminDashboardPage(): JSX.Element {
  const user_role = useAuthStore((state) => state.user_role);
  const setError = useGlobalStore((state) => state.setError);
  const { stats, byAgency, getStats, getByAgency } = useAdminStatsStore();
  const [isLoading, setIsLoading] = useState(false);
  const [metric, setMetric] = useState<AgencyMetric>("campaigns");

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        await Promise.all([getStats(), getByAgency()]);
      } catch (error: any) {
        setError(error.message ?? "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Global-Admin-only page
  if (user_role && user_role !== "Global Admin") {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Layout>
      <h1 className="text-2xl md:text-3xl font-semibold text-black mb-2">
        Admin Dashboard
      </h1>
      <p className="text-gray-500 mb-8">Platform-wide totals as of now.</p>

      {isLoading || !stats ? (
        <div className="flex justify-center items-center h-64">
          <Spinner size={40} />
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          {buildSections(stats).map((section) => (
            <div key={section.title}>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                {section.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {section.cards.map((c) => (
                  <StatCard key={c.label} {...c} />
                ))}
              </div>
            </div>
          ))}

          {/* Per-agency breakdown: bar + interactive donut for the chosen metric */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  By Media Agency
                </h2>
                <p className="text-sm text-gray-500">
                  Which agency has which{" "}
                  {METRICS.find((m) => m.key === metric)?.label.toLowerCase()}.
                  Click a legend item to include/exclude it.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMetric(m.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      metric === m.key
                        ? "bg-[#EA33C0] text-white border-[#EA33C0]"
                        : "bg-white text-gray-600 border-gray-300 hover:border-[#EA33C0] hover:text-[#EA33C0]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <BarChart
                data={toChartData(byAgency ?? [], metric)}
                emptyText="No agencies yet."
              />
              <DonutChart
                key={metric}
                data={toChartData(byAgency ?? [], metric)}
                emptyText="No agencies yet."
              />
            </div>
          </div>

          {/* Composition donuts (interactive legends) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Catalog Composition
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Share of catalog entities.
              </p>
              <DonutChart data={catalogSlices(stats)} />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                Delivery Composition
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Campaigns vs flights vs assets.
              </p>
              <DonutChart data={deliverySlices(stats)} />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                People
              </h2>
              <p className="text-sm text-gray-500 mb-5">Users vs teams.</p>
              <DonutChart data={peopleSlices(stats)} />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
